/**
 * Comprehensive Sentry Logger
 *
 * A powerful yet easy-to-use Sentry logging utility that automatically captures:
 * - Device information (browser, OS, screen size, etc.)
 * - User context and attributes
 * - Location/country information
 * - App version and environment
 * - User actions and breadcrumbs
 * - Custom context data
 *
 * @example
 * ```typescript
 * // Breadcrumb logging (does NOT create Sentry events - minimal noise)
 * logger.info("User clicked checkout button");
 * logger.warning("Cart item out of stock");
 *
 * // Error logging (DOES create Sentry events)
 * logger.error("Payment failed", { paymentId: "123", amount: 100 });
 * logger.captureError(error, { orderId: "456" });
 *
 * // Explicit event capture (use sparingly)
 * logger.captureMessage("Critical milestone reached", "info", { milestone: "checkout" });
 *
 * // Track user actions (breadcrumbs only)
 * logger.trackAction("button_click", { buttonId: "checkout" });
 * ```
 */

import * as Sentry from "@sentry/react";
import { getCurrentUserId } from "../api/amplifyAuth";
import {
  getAppVersion,
  getActiveCountry,
  getBrowsingCountry,
  getEnvironmentType
} from "./utils";

/**
 * Device information interface
 */
interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  viewport: {
    width: number;
    height: number;
  };
  userAgent: string;
  language: string;
  timezone: string;
  online: boolean;
  cookieEnabled: boolean;
  doNotTrack: string | null;
}

/**
 * Location information interface
 */
interface LocationInfo {
  activeCountry: string;
  browsingCountry: string;
  timezone: string;
  language: string;
}

/**
 * User context interface
 */
interface UserContext {
  userId: string | null;
  userAttributes?: any;
  sessionId?: string;
  isAuthenticated: boolean;
}

/**
 * Log context interface
 */
interface LogContext {
  [key: string]: any;
}

/**
 * Sentry log levels
 */
type LogLevel = "fatal" | "error" | "warning" | "info" | "debug";

/**
 * SentryLogger class - Comprehensive logging utility
 */
class SentryLogger {
  private static instance: SentryLogger;
  private deviceInfo: DeviceInfo | null = null;
  private sessionId: string;
  private isInitialized: boolean = false;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeDeviceInfo();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): SentryLogger {
    if (!SentryLogger.instance) {
      SentryLogger.instance = new SentryLogger();
    }
    return SentryLogger.instance;
  }

  /**
   * Initialize the logger with user context
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      const userId = await getCurrentUserId();
      const userContext = await this.getUserContext();
      const locationInfo = this.getLocationInfo();

      // Set user context in Sentry - only set if we have a userId
      if (userId) {
        Sentry.setUser({
          id: userId,
          username: userId,
        });
      }

      // Set tags for filtering in Sentry
      Sentry.setTags({
        app_version: getAppVersion(),
        environment: getEnvironmentType(),
        active_country: locationInfo.activeCountry,
        browsing_country: locationInfo.browsingCountry,
        session_id: this.sessionId,
        browser: this.deviceInfo?.browser ?? "unknown",
        os: this.deviceInfo?.os ?? "unknown",
      });

      // Set context data
      Sentry.setContext("device", this.deviceInfo as any);
      Sentry.setContext("location", locationInfo);
      Sentry.setContext("user_details", userContext);

      this.isInitialized = true;
      this.addBreadcrumb("Logger initialized", "info", { userId: userId ?? "not_authenticated" });
    } catch (error) {
      console.error("Failed to initialize Sentry logger:", error);
    }
  }

  /**
   * Update user context (call this when user logs in/out or attributes change)
   */
  public async updateUserContext(): Promise<void> {
    try {
      const userId = await getCurrentUserId();
      const userContext = await this.getUserContext();

      // Properly set user ID - important for Sentry to identify users
      if (userId) {
        Sentry.setUser({
          id: userId,
          username: userId,
        });
      } else {
        // If no user, clear the user context
        Sentry.setUser(null);
      }

      Sentry.setContext("user_details", userContext);

      this.addBreadcrumb("User context updated", "info", { userId: userId ?? "cleared" });
    } catch (error) {
      console.error("Failed to update user context:", error);
    }
  }

  /**
   * Clear user context (call on logout)
   */
  public clearUserContext(): void {
    Sentry.setUser(null);
    this.addBreadcrumb("User logged out", "info");
  }

  /**
   * Log info message (creates breadcrumb only, does not create Sentry event)
   */
  public info(message: string, context?: LogContext): void {
    this.addBreadcrumb(message, "info", context, "log");
  }

  /**
   * Log warning message (creates breadcrumb only, does not create Sentry event)
   */
  public warning(message: string, context?: LogContext): void {
    this.addBreadcrumb(message, "warning", context, "log");
  }

  /**
   * Log error message
   */
  public error(message: string, context?: LogContext): void {
    this.captureMessage(message, "error", context);
  }

  /**
   * Log debug message (only in development)
   */
  public debug(message: string, context?: LogContext): void {
    if (getEnvironmentType() !== "production") {
      this.captureMessage(message, "debug", context);
    }
  }

  /**
   * Capture an exception/error with full context
   */
  public captureError(
    error: Error | any,
    context?: LogContext,
    level: LogLevel = "error"
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(level);

      // Add custom context
      if (context) {
        scope.setContext("error_context", context);
      }

      // Add device and location info
      scope.setContext("device", this.deviceInfo as any);
      scope.setContext("location", this.getLocationInfo() as any);

      // Add timestamp
      scope.setTag("error_timestamp", new Date().toISOString());

      // Capture the error
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureException(new Error(JSON.stringify(error)));
      }
    });
  }

  /**
   * Track user action (creates breadcrumb)
   */
  public trackAction(
    action: string,
    data?: Record<string, any>,
    category: string = "user"
  ): void {
    this.addBreadcrumb(action, "info", data, category);
  }

  /**
   * Track navigation (creates breadcrumb)
   */
  public trackNavigation(from: string, to: string): void {
    this.addBreadcrumb(
      `Navigation: ${from} -> ${to}`,
      "info",
      { from, to },
      "navigation"
    );
  }

  /**
   * Track API call
   */
  public trackApiCall(
    method: string,
    url: string,
    status?: number,
    data?: any
  ): void {
    this.addBreadcrumb(
      `API ${method} ${url}`,
      status && status >= 400 ? "error" : "info",
      { method, url, status, data },
      "http"
    );
  }

  /**
   * Add custom breadcrumb
   */
  public addBreadcrumb(
    message: string,
    level: Sentry.SeverityLevel = "info",
    data?: Record<string, any>,
    category: string = "custom"
  ): void {
    Sentry.addBreadcrumb({
      message,
      level,
      category,
      data: {
        ...data,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Set custom tag
   */
  public setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  /**
   * Set multiple custom tags
   */
  public setTags(tags: Record<string, string>): void {
    Sentry.setTags(tags);
  }

  /**
   * Set custom context
   */
  public setContext(name: string, context: Record<string, any>): void {
    Sentry.setContext(name, context);
  }

  /**
   * Explicitly capture a message as a Sentry event (creates a new event)
   * Use this sparingly - only when you need a standalone event.
   * For general logging, use info() or warning() which create breadcrumbs instead.
   */
  public captureMessage(
    message: string,
    level: LogLevel = "info",
    context?: LogContext
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(level);

      // Add custom context
      if (context) {
        scope.setContext("message_context", context);
      }

      // Add device and location info
      scope.setContext("device", this.deviceInfo as any);
      scope.setContext("location", this.getLocationInfo() as any);

      // Add timestamp tag
      scope.setTag("log_timestamp", new Date().toISOString());

      Sentry.captureMessage(message);
    });
  }

  /**
   * Get device information
   */
  private initializeDeviceInfo(): void {
    try {
      const ua = navigator.userAgent;
      const { browser, browserVersion } = this.parseBrowserInfo(ua);
      const { os, osVersion } = this.parseOSInfo(ua);

      this.deviceInfo = {
        browser,
        browserVersion,
        os,
        osVersion,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        devicePixelRatio: window.devicePixelRatio,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        userAgent: ua,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
      };
    } catch (error) {
      console.error("Failed to initialize device info:", error);
      this.deviceInfo = null;
    }
  }

  /**
   * Parse browser information from user agent
   */
  private parseBrowserInfo(ua: string): { browser: string; browserVersion: string } {
    let browser = "Unknown";
    let browserVersion = "Unknown";

    // Chrome
    if (ua.indexOf("Chrome") > -1 && ua.indexOf("Edg") === -1) {
      browser = "Chrome";
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    }
    // Safari
    else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) {
      browser = "Safari";
      const match = ua.match(/Version\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    }
    // Firefox
    else if (ua.indexOf("Firefox") > -1) {
      browser = "Firefox";
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    }
    // Edge
    else if (ua.indexOf("Edg") > -1) {
      browser = "Edge";
      const match = ua.match(/Edg\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    }

    return { browser, browserVersion };
  }

  /**
   * Parse OS information from user agent
   */
  private parseOSInfo(ua: string): { os: string; osVersion: string } {
    let os = "Unknown";
    let osVersion = "Unknown";

    // Windows
    if (ua.indexOf("Windows") > -1) {
      os = "Windows";
      if (ua.indexOf("Windows NT 10.0") > -1) osVersion = "10";
      else if (ua.indexOf("Windows NT 6.3") > -1) osVersion = "8.1";
      else if (ua.indexOf("Windows NT 6.2") > -1) osVersion = "8";
      else if (ua.indexOf("Windows NT 6.1") > -1) osVersion = "7";
    }
    // macOS
    else if (ua.indexOf("Mac OS X") > -1) {
      os = "macOS";
      const match = ua.match(/Mac OS X (\d+[._]\d+)/);
      osVersion = match ? match[1].replace("_", ".") : "Unknown";
    }
    // iOS
    else if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) {
      os = ua.indexOf("iPhone") > -1 ? "iOS" : "iPadOS";
      const match = ua.match(/OS (\d+[._]\d+)/);
      osVersion = match ? match[1].replace("_", ".") : "Unknown";
    }
    // Android
    else if (ua.indexOf("Android") > -1) {
      os = "Android";
      const match = ua.match(/Android (\d+\.\d+)/);
      osVersion = match ? match[1] : "Unknown";
    }
    // Linux
    else if (ua.indexOf("Linux") > -1) {
      os = "Linux";
    }

    return { os, osVersion };
  }

  /**
   * Get location information
   */
  private getLocationInfo(): LocationInfo {
    return {
      activeCountry: getActiveCountry(),
      browsingCountry: getBrowsingCountry(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    };
  }

  /**
   * Get user context
   */
  private async getUserContext(): Promise<UserContext> {
    const userId = await getCurrentUserId();

    return {
      userId,
      sessionId: this.sessionId,
      isAuthenticated: userId !== null,
    };
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get device info
   */
  public getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }
}

// Export singleton instance
export const logger = SentryLogger.getInstance();

// Export class for type usage
export { SentryLogger };

// Export types
export type { DeviceInfo, LocationInfo, UserContext, LogContext, LogLevel };
